// The ONE sections-and-fields renderer.
//
// Four screens draw a form's sections: the fill page, the builder's preview,
// the versions viewer and the submission read-back. Until the 2026-09-29
// restyle each hand-rolled its own loop with slightly different markup, which
// is the same drift one layer up that FormFieldInput exists to prevent one
// layer down. A source-reading test now fails if any of the four maps
// `content.sections` itself.
//
// Each section is a CARD: on a long prestart the bare headings washed out and
// the section boundaries disappeared (ZT ships the card shape). An untitled
// section still gets the card, so the grouping survives the missing name.
import type { FormContent, SubmissionFile } from '../../formsApi';
import { FormFieldInput, type MeterOption } from './FormFieldInput';
import type { FormFileSource } from './fileSource';

export function FormSections({
  content,
  answers,
  files,
  meters,
  refOptions,
  submissionId,
  readOnly,
  problems,
  signedCaption,
  onChange,
  onFilesChanged,
  fileSource,
}: {
  content: FormContent;
  answers: Record<string, unknown>;
  files: SubmissionFile[];
  meters: MeterOption[];
  refOptions?: Record<string, { id: string; label: string }[]>;
  submissionId: string;
  readOnly: boolean;
  /** Field key → marker sentence: the refused-submit markers, computed live
      by the page so answering a question clears its own marker. */
  problems?: ReadonlyMap<string, string>;
  /** The read-back's who-and-when line under a signature. */
  signedCaption?: string | null;
  onChange?: (key: string, value: unknown) => void;
  onFilesChanged?: () => void;
  /** Where photos come from and go to; the server unless the page says. */
  fileSource?: FormFileSource;
}) {
  return (
    <div>
      {content.sections.map((section) => (
        <section key={section.key} className="card mb-4 px-4 py-5 sm:px-5">
          {section.title && (
            <h2 className="mb-3 text-base font-semibold text-gray-900">{section.title}</h2>
          )}
          {section.fields.map((field) => (
            <FormFieldInput
              key={field.key}
              field={field}
              value={answers[field.key]}
              files={files}
              meters={meters}
              refOptions={refOptions?.[field.key]}
              submissionId={submissionId}
              readOnly={readOnly}
              problem={problems?.get(field.key) ?? null}
              signedCaption={field.type === 'signature' ? signedCaption : undefined}
              onChange={(value) => onChange?.(field.key, value)}
              onFilesChanged={() => onFilesChanged?.()}
              fileSource={fileSource}
            />
          ))}
          {section.fields.length === 0 && <p className="muted">No questions in this section.</p>}
        </section>
      ))}
    </div>
  );
}
